import { CardFace, faceH } from "@/components/game/HandRow";
import { ActionPile, DeckPile, DiscardPile } from "@/components/game/Piles";

// MARKETPLACE (right column, top): the 4 face-up build cards in one row; under them the
// BUILD CARD DECK (blind pick during a pick), ACTION CARD DECK (one click = take an action
// card) and DISCARD PILE, and on the right the Wipe market button.
// ruleDim: outside a market pick the market + deck sit at 45% (taking is blocked; viewing works).
const RULE_DIM = { opacity: 0.45, filter: "saturate(0.6)" };
export const MARKET_HEAD_H = 20;

// PAY UPFRONT: cards are paid when taken — the market card's price at once, the blind card once
// it is seen, an action card 3. pickHint: the header text during a pick (nothing affordable, in
// debt, a blind card to pay). eventDecks: the weather + global draw piles (v3 step 4) and the Climate Bank tab, shown under the piles (the "one weather card and one global card each round" note is the row's tooltip). skip: { enabled, reason, onClick } — skip the take (no affordable card).
export default function MarketPanel({ market, mode, canDraw, viewedIdx, cardCost, cardW = 104, onView, onHover, deckCount, canDrawDeck = false, deckReason = null, onDrawDeck, discardPile, onOpenDiscard, actionCount = 0, actionDiscardCount = 0, canTakeAction = false, takeActionReason = null, onTakeAction, onTakeBlocked, actionPrice = 3, wipe = null, skip = null, pickHint = null, ruleDim = false, focusDim = false, eventDecks = null }) {
  const focus = { opacity: focusDim ? 0.5 : 1 };
  const rule = ruleDim ? RULE_DIM : focus;
  const gap = 10;
  return (
    <section data-testid="market-row" data-rule-dim={ruleDim ? "true" : "false"} className="shrink-0 flex flex-col min-w-0" style={{ padding: "8px 12px 10px", borderRadius: 14, background: "var(--card)", border: "1px solid var(--line)" }}>
      <div className="focus-region shrink-0 flex items-baseline gap-2 min-w-0 whitespace-nowrap" style={{ height: 16, lineHeight: "16px", marginBottom: 7, ...focus }}>
        <span className="section-label shrink-0">Marketplace</span>
        <span data-testid="market-hint" className="truncate min-w-0" title={pickHint || undefined} style={{ fontSize: 12, color: canDraw ? "var(--sea)" : "var(--ink-5)", fontWeight: canDraw ? 700 : 400 }}>
          {pickHint || (canDraw ? "Take one (pay its price), or draw blind" : "Take 1 after each build card · pay when you take it")}
        </span>
      </div>
      {mode === "setup" ? (
        <div data-testid="market-setup-hidden" className="shrink-0 flex items-center justify-center text-center" style={{ height: faceH(cardW), borderRadius: "var(--radius)", border: "1.5px dashed #B9B3A6", color: "var(--ink-4)", fontSize: 13 }}>
          The market opens after Phase 1.
        </div>
      ) : (
        <div data-testid="market-cards" className="focus-region shrink-0 flex flex-nowrap" style={{ gap, ...rule }}>
          {market.map((card, i) => (
            <CardFace
              key={card.id}
              card={card}
              w={cardW}
              testid={`market-card-${i}-${card.kind}`}
              nameTestid={`market-card-name-${i}`}
              statsTestid={`market-card-stats-${i}`}
              statTestid={`market-card-stat-${i}`}
              selected={viewedIdx === i}
              cost={cardCost(card)}
              costTestid={`market-cost-${i}`}
              onClick={() => onView(i)}
              onHover={onHover}
            />
          ))}
        </div>
      )}
      <div data-testid="piles-row" className="shrink-0 flex items-start" style={{ marginTop: 10, gap: 12 }}>
        <div className="focus-region" style={rule}>
          <DeckPile count={deckCount} enabled={canDrawDeck} reason={canDraw ? deckReason : null} onTake={onDrawDeck} onBlocked={onTakeBlocked} />
        </div>
        <div className="focus-region" style={focus}>
          <ActionPile count={actionCount} discardCount={actionDiscardCount} enabled={canTakeAction} reason={takeActionReason} price={actionPrice} onTake={onTakeAction} onBlocked={onTakeBlocked} />
        </div>
        <div className="focus-region" style={focus}>
          <DiscardPile pile={discardPile} onOpen={onOpenDiscard} />
        </div>
        {wipe && (
          <div data-testid="wipe-box" className="focus-region ml-auto flex flex-col items-stretch" style={{ width: 128, gap: 6, paddingTop: 4, ...focus }}>
            <span style={{ fontSize: 11.5, lineHeight: "15px", color: "var(--ink-3)" }}>Swap all four market cards for new ones.</span>
            <button data-testid="btn-wipe-market" onClick={wipe.onClick} disabled={!wipe.enabled} className="btn-secondary btn-compact btn-card w-full" style={{ height: 30 }} title={wipe.reason || `Pay ${wipe.cost} to lay out 4 new market cards (once per turn)`}>
              Wipe market ({wipe.cost} coins)
            </button>
            {!wipe.enabled && wipe.reason && !skip && (
              <span data-testid="wipe-reason" style={{ fontSize: 10.5, lineHeight: "13px", color: "var(--ink-4)" }}>{wipe.reason}</span>
            )}
            {skip && (
              <>
                <button data-testid="btn-skip-pick" onClick={skip.onClick} disabled={!skip.enabled} className="btn-secondary btn-compact btn-card w-full" style={{ height: 30 }} title={skip.reason || "Take no card this time (you can't afford one)"}>
                  Skip taking a card
                </button>
                {!skip.enabled && skip.reason && (
                  <span data-testid="skip-pick-reason" style={{ fontSize: 10.5, lineHeight: "13px", color: "var(--ink-4)" }}>{skip.reason}</span>
                )}
              </>
            )}
          </div>
        )}
      </div>
      {eventDecks && (
        <div data-testid="event-decks-row" title="At the end of every round the app draws one weather card and one global card." className="focus-region shrink-0 flex items-start min-w-0" style={{ marginTop: 8, paddingTop: 8, gap: 16, borderTop: "1px solid var(--line-soft)" }}>
          {eventDecks}
        </div>
      )}
    </section>
  );
}
